import ReportIssueBar from '@/components/ReportIssueBar';
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import toast from 'react-hot-toast';
import { Upload, Download, FileText, Sparkles, ArrowLeft, TrendingUp, Eye, X, Menu, Settings } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import MatrixBackground from "@/MatrixBackground";
import AnimatedToolTitle from '@/components/AnimatedToolTitle';
import AnimatedActionButton from '@/components/AnimatedActionButton';
import AccountSettings from '@/components/AccountSettings';
import LedProgressLine from "@/components/LedProgressLine";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getToken = () => {
  const token = localStorage.getItem('token');
  if (token) return token;
  const trialUser = localStorage.getItem('trial_user');
  const trialId = localStorage.getItem('trial_id');
  if (trialUser && trialId) return `trial_user_${trialId}`;
  return null;
};

const getUser = () => {
  const user = localStorage.getItem('user');
  return user ? JSON.parse(user) : null;
};

const ContractReviewTool = ({ onBack }) => {
    // Set page title
  useEffect(() => {
    document.title = "Contract Review - Lawra AI";
    return () => {
      document.title = "Lawra AI - AI Legal Assistant for Lawyers & Law Firms | Save 70-80% Time";
    };
  }, []);

  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [customQuestions, setCustomQuestions] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [user, setUser] = useState(getUser());
  const [trialUser, setTrialUser] = useState(null);
  const [accountSettingsOpen, setAccountSettingsOpen] = useState(false);
  const [accountSettingsTab, setAccountSettingsTab] = useState('account');

  useEffect(() => {
    // Get trial user data
    const trialData = localStorage.getItem('trial_user');
    if (trialData) {
      setTrialUser(JSON.parse(trialData));
    }
  }, []);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleAnalyze = async () => {
    if (!file) {
      toast.error('Please upload a contract file');
      return;
    }

    const token = getToken();
    console.log('[CONTRACT] Token:', token);
    console.log('[CONTRACT] localStorage.token:', localStorage.getItem('token'));
    console.log('[CONTRACT] localStorage.trial_user:', localStorage.getItem('trial_user'));
    console.log('[CONTRACT] localStorage.trial_id:', localStorage.getItem('trial_id'));

    setAnalyzing(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('custom_questions', customQuestions);

    try {
      const response = await axios.post(`${API}/contracts/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 180000 // 3 minute timeout
      });

      setSelectedContract(response.data);
      toast.success('Contract analysed successfully by Lawra!');
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Error analysing contract. Please try again.';
      
      // Check if it's a trial limit error
      if (error.response?.status === 403 && errorMessage.includes('Trial limit')) {
        // Open Account Settings modal on Billing tab
        toast.error(errorMessage);
        setTimeout(() => {
          // Navigate to dashboard with query params to open account settings
          navigate('/dashboard?open=account-settings&tab=billing');
        }, 500);
      } else {
        toast.error(errorMessage);
      }
      
      console.error('Analysis error:', error);
    } finally {
      setAnalyzing(false);
    }
  };

  const generateWordDocument = async () => {
    if (!selectedContract) {
      toast.error('No contract data to export');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/contracts/generate-word`,
        { contract: selectedContract },
        { 
          responseType: 'blob',
          headers: { Authorization: `Bearer ${getToken()}` }
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedContract.filename}_analysis.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Word document downloaded successfully!');
    } catch (error) {
      console.error('Error generating Word document:', error);
      toast.error('Error generating Word document');
    }
  };

  const handlePreview = async () => {
    if (!file) return;
    
    try {
      const text = await file.text();
      setPreviewContent(text);
      setShowPreview(true);
    } catch (error) {
      console.error('Error previewing file:', error);
      toast.error('Could not preview file');
    }
  };

  const handleReset = () => {
    setFile(null);
    setCustomQuestions('');
    setSelectedContract(null);
  };

  const handleLogout = () => {
    if (trialUser) {
      navigate('/auth', { state: { showRegister: true, trialUserData: trialUser } });
    } else {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/');
    }
  };

  return (
    <div className="tool-page">
      <MatrixBackground />
      
      {/* Dashboard Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <img src="/lawra-header-logo.png" alt="Lawra" className="header-logo-image" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} />
          </div>
          
          <div className="header-right">
            {/* Mobile-only logo */}
            <img src="/lawra-header-logo.png" alt="Lawra" className="header-logo-image mobile-only" />
            
            <span className="user-name">
              Hello, <span className="gold-text">{trialUser?.name || user?.full_name}</span>
            </span>
            {trialUser && (
              <span className="trial-badge">Trial Mode</span>
            )}
            
            <Button 
              onClick={handleLogout}
              style={{
                background: 'linear-gradient(135deg, #D4AF37 0%, #E5B200 100%)',
                color: '#1a1a1a',
                border: 'none',
                fontWeight: '700',
                padding: '0.75rem 2rem',
                borderRadius: '50px',
                boxShadow: '0 4px 15px rgba(212, 175, 55, 0.4)',
                animation: 'signupPulse 2s ease-in-out infinite',
                whiteSpace: 'nowrap',
                minWidth: '100px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #FFCE45 0%, #E5B200 100%)';
                e.currentTarget.style.boxShadow = '0 8px 30px rgba(212, 175, 55, 0.8)';
                e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                e.currentTarget.style.animation = 'none';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #D4AF37 0%, #E5B200 100%)';
                e.currentTarget.style.boxShadow = '0 4px 15px rgba(212, 175, 55, 0.4)';
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.animation = 'signupPulse 2s ease-in-out infinite';
              }}
            >
              {trialUser ? 'Sign Up' : 'Logout'}
            </Button>

            {/* Menu Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="menu-button">
                  <Menu size={20} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="menu-dropdown">
                <DropdownMenuItem onClick={() => setAccountSettingsOpen(true)}>
                  <Settings size={16} className="mr-2" />
                  Account Settings
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
      
      <div className="tool-header">
        <Button
          variant="ghost"
          onClick={() => onBack ? onBack() : navigate('/dashboard')}
          className="back-button"
        >
          <ArrowLeft size={20} />
          Back to Dashboard
        </Button>
        <div className="tool-title-section">
          <FileText size={40} className="tool-icon gold-icon" />
          <div>
            <AnimatedToolTitle title="Document Review" />
            <p className="tool-subtitle">Upload and analyse contracts with Lawra&apos;s AI assistance</p>
          </div>
        </div>
      </div>

      <div className="tool-content">
        {!selectedContract ? (
          <div className="tool-grid">
            <Card className="generation-form-card">
              <CardHeader>
                <CardTitle>Document Upload & Analysis</CardTitle>
                <CardDescription>Upload your contract for comprehensive AI analysis</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="form-group">
                  <label className="upload-label">Contract File *</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="file-input-contract-review"
                      accept=".pdf,.docx,.doc"
                      onChange={handleFileChange}
                      style={{ display: 'none' }}
                    />
                    <label htmlFor="file-input-contract-review" className="file-upload-label">
                      <div className="upload-icon-tool">
                        {file ? <FileText size={48} /> : <Upload size={48} />}
                      </div>
                      <p className="upload-text-tool">
                        {file ? file.name : 'Click to upload contract (PDF or DOCX)'}
                      </p>
                      <p className="upload-subtext-tool">Maximum file size: 10MB</p>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Custom Questions (Optional)</label>
                  <textarea
                    value={customQuestions}
                    onChange={(e) => setCustomQuestions(e.target.value)}
                    placeholder="Enter any specific questions about the contract (one per line)"
                    rows={4}
                    className="form-textarea"
                  />
                </div>

                <div className="form-actions">
                  <AnimatedActionButton
                    onClick={handleAnalyze}
                    disabled={!file || analyzing}
                    loading={analyzing}
                    icon={<Sparkles size={20} />}
                  >
                    {analyzing ? 'Analysing Contract...' : 'Analyse Contract'}
                  </AnimatedActionButton>
                  <LedProgressLine isActive={analyzing} />
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          <>
            {/* Review Results Header */}
            <div className="results-header">
              <h2>Document Analysis Results</h2>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <Button onClick={generateWordDocument} variant="outline">
                  <Download size={18} />
                  Export to Word
                </Button>
                <Button onClick={handleReset} variant="outline">
                  <ArrowLeft size={18} />
                  New Analysis
                </Button>
              </div>
            </div>

            {/* Summary Card */}
            <Card className="result-card">
              <CardHeader>
                <CardTitle>{selectedContract.filename}</CardTitle>
                <CardDescription>
                  Analysed on {new Date(selectedContract.created_at).toLocaleString()}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="section-content" dangerouslySetInnerHTML={{ __html: selectedContract.summary }} />
              </CardContent>
            </Card>

            {/* Analysis Sections Container */}
            <div className="editor-container">
              <div className="editor-section">
                {/* Risk Assessment */}
                <Card className="result-card">
                  <CardHeader>
                    <CardTitle style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <TrendingUp size={24} /> Risk Assessment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="section-content" dangerouslySetInnerHTML={{ __html: selectedContract.risk_assessment }} />
                  </CardContent>
                </Card>

                {/* Clause Analysis */}
                <Card className="result-card" style={{ marginTop: '1.5rem' }}>
                  <CardHeader>
                    <CardTitle>Key Clauses Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="section-content" dangerouslySetInnerHTML={{ __html: selectedContract.clause_analysis }} />
                  </CardContent>
                </Card>

                {/* Key Terms */}
                <Card className="result-card" style={{ marginTop: '1.5rem' }}>
                  <CardHeader>
                    <CardTitle>Key Terms</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="section-content" dangerouslySetInnerHTML={{ __html: selectedContract.key_terms }} />
                  </CardContent>
                </Card>

                {/* Compliance Check */}
                <Card className="result-card" style={{ marginTop: '1.5rem' }}>
                  <CardHeader>
                    <CardTitle>Compliance Check</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="section-content" dangerouslySetInnerHTML={{ __html: selectedContract.compliance_check }} />
                  </CardContent>
                </Card>

                {/* Custom Questions */}
                {selectedContract.custom_questions && selectedContract.custom_questions.length > 0 && (
                  <Card className="result-card" style={{ marginTop: '1.5rem' }}>
                    <CardHeader>
                      <CardTitle>Custom Questions & Answers</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedContract.custom_questions.map((qa, index) => (
                        <div key={index} className="qa-item">
                          <div className="question">
                            <strong>Q{index + 1}:</strong> {qa.question}
                          </div>
                          <div className="answer section-content" dangerouslySetInnerHTML={{ __html: qa.answer }} />
                          {qa.clause_reference && (
                            <div className="clause-ref">
                              <em>Reference: {qa.clause_reference}</em>
                            </div>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </>
        )}
        <ReportIssueBar toolName="Contract Review" />
      </div>

      {/* File Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>File Preview</DialogTitle>
          </DialogHeader>
          <div className="preview-content">
            <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>{previewContent}</pre>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Account Settings Modal */}
      <AccountSettings
        open={accountSettingsOpen}
        onOpenChange={setAccountSettingsOpen}
        user={user}
        trialUser={trialUser}
        defaultTab={accountSettingsTab}
      />
    </div>
  );
};

export default ContractReviewTool;
